<template>
  <component :is="overlayComponent" v-bind="overlayProps">
    <template v-slot:activator="{ props }">
      <span
        v-bind="props"
        class="info-button-wrapper"
        :role="openOnClick ? 'button' : undefined"
        :tabindex="openOnClick ? 0 : undefined"
        :aria-label="openOnClick ? ariaLabel : undefined"
      >
        <Info class="info-icon" :class="{ 'info-icon-disabled': disabled }" />
      </span>
    </template>
    <template v-slot:default>
      <!-- Klick-Modus: echtes Popover mit sichtbarem Schließen-Button. -->
      <v-card v-if="openOnClick" class="info-popover" elevation="6">
        <div class="info-popover__text" v-html="tooltip" />
        <button
          type="button"
          class="info-popover__close"
          :aria-label="$gt('Schließen')"
          @click="isOpen = false"
        >
          <X :size="16" />
        </button>
      </v-card>
      <div v-else style="padding: 6px" v-html="tooltip" />
    </template>
  </component>
</template>

<script>
import { Info, X } from "lucide-vue-next";
import { VMenu, VTooltip } from "vuetify/components";

export default {
  name: "InfoButton",

  props: {
    tooltip: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Erklärung erst beim Klick zeigen (Popover) statt beim Hover (Tooltip). */
    openOnClick: {
      type: Boolean,
      default: false,
    },
    /** Beschriftung für Screenreader, wenn der Info-Punkt klickbar ist. */
    ariaLabel: {
      type: String,
      default: "Erklärung anzeigen",
    },
  },

  components: {
    Info,
    X,
  },

  data() {
    return {
      isOpen: false,
    };
  },

  computed: {
    /** Klick-Modus rendert ein Popover, Hover-Modus den klassischen Tooltip. */
    overlayComponent() {
      return this.openOnClick ? VMenu : VTooltip;
    },

    /** Popover und Tooltip teilen sich den Activator, nicht aber ihre Props. */
    overlayProps() {
      if (!this.openOnClick) return { maxWidth: 480 };
      return {
        maxWidth: 360,
        modelValue: this.isOpen,
        "onUpdate:modelValue": (v) => {
          this.isOpen = v;
        },
        closeOnContentClick: false,
        location: "top",
        offset: 6,
      };
    },
  },
};
</script>

<style scoped>
.info-button-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.2s ease;
  vertical-align: middle;
}

.info-button-wrapper:hover {
  background-color: var(--grey-medium-color);
}

.info-icon {
  width: 18px;
  height: 18px;
  stroke-width: 2;
  color: var(--primary-color);
}

.info-icon-disabled {
  color: var(--disabled-grey-color);
}

.info-popover {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 10px 10px 12px;
  border-radius: 8px;
}

.info-popover__text {
  font-size: 12.5px;
  line-height: 1.45;
  color: #334155;
}

/* Links im Popover (z. B. "Mehr zur App →", Kundenfeedback 05.09.26, V). */
.info-popover__text :deep(a) {
  color: var(--primary-color, #0076e5);
  font-weight: 600;
  text-decoration: none;
}

.info-popover__text :deep(a:hover) {
  text-decoration: underline;
}

.info-popover__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #64748b;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.info-popover__close:hover {
  background-color: #eef2f7;
  color: #0f172a;
}
</style>
